<script setup lang="ts">
/*
* Компонент табличного отображения списка нежелательных событий.
*
* Основные функции:
* - Отображает список событий с пагинацией
* - Поддерживает множественный выбор и массовое удаление
* - Предоставляет кнопки действий для каждого события (просмотр, редактирование, удаление)
* - Позволяет сбрасывать фильтры и обновлять данные
* - Поддерживает экспорт данных в CSV
* - Реализует бесконечную подгрузку при скролле
*/

// Импорты Vue Router
import { useRoute, useRouter } from 'vue-router'

// Импорты Vuetify
import { useDisplay, useTheme } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

// Импорты хранилищ
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
// Импорты утилит и констант
import { useExportUrl } from '@/composables/useExportUrl'
import { ERouteNames } from '@/router/ERouteNames'
import { formatDateOnly, formatTimeOnly } from '@/utils/formatters'
import { useTableHideCompleted } from '@/composables/useTableHideCompleted'

// Импорты типов
import type { IAdverseEvent } from '@/modules/apiStore/types/adverse-events/IAdverseEvent'

// Импорты компонентов
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import AdverseSearchFilter from '@/modules/adverseEvents/components/AdverseSearchFilter.vue'
import { adverseReportFilters } from '@/modules/adverseEvents/utils/reportFilters'
import { adverseSearchState } from '@/modules/adverseEvents/utils/searchFilters'

import AdverseEventTypesModal from '@/components/AdverseEventTypesModal.vue'
import AdverseProbabilityModal from '@/modules/adverseEvents/components/settings/AdverseProbabilityModal.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { ADVERSE_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/adverseEvents/adapter'
import { ADVERSE_ANCHORS, ADVERSE_CONTEXTS } from '@/modules/onboarding/scenarios/adverseEvents/anchors'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { ONBOARDING_DEMO_QUERY } from '@/modules/onboarding/utils/demoRoute'

// Импорт Composable
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'

defineEmits(['click:prev', 'click:next', 'click:first', 'click:last'])

// Инициализация утилит отображения Vuetify
const { smAndDown } = useDisplay()

// Инициализация хранилищ и роутера
const router = useRouter() // Для навигации между страницами
const apiStore = useApiStore() // Хранилище данных API нежелательных событий
const permissionsStore = usePermissionsStore() // Хранилище прав доступа
const feedbackStore = useFeedbackStore()// Хранилище состояния UI (загрузки, тосты)
const theme = useTheme() // Текущая тема Vuetify

const searchFilterRef = ref<InstanceType<typeof AdverseSearchFilter> | null>(null)
const isAdminSettingsOpen = ref(false)
const isProbabilitySettingsOpen = ref(false)
const route = useRoute()

// Настройки из шестерёнки бокового меню: событием на этой странице или ?settings= при переходе с другой; права — как у кнопок «Настройки»
const openSettingsModal = (kind: unknown) => {
  if (kind === 'types' && permissionsStore.modules.adverse?.type_edit)
    isAdminSettingsOpen.value = true
  else if (kind === 'probability' && permissionsStore.modules.adverse?.probability)
    isProbabilitySettingsOpen.value = true
}

const handleAdverseSettingsEvent = (e: Event) => openSettingsModal((e as CustomEvent<string>).detail)

onMounted(() => {
  window.addEventListener('open-adverse-settings', handleAdverseSettingsEvent)
  openSettingsModal(route.query.settings)
})

watch(() => route.query.settings, openSettingsModal)

onUnmounted(() => {
  window.removeEventListener('open-adverse-settings', handleAdverseSettingsEvent)
})

// Извлекаем Refs для двусторонней синхронизации пагинации и сортировки
const { currentSortString, nextAdverseEventsCursor } = storeToRefs(apiStore)
const { itemsPerPage } = useTableItemsPerPage('adverseEvents', 25)
const { hideCompleted } = useTableHideCompleted('adverseEvents')
apiStore.filters.is_actual = hideCompleted.value ? true : null

// Синхронизация с хранилищем нежелательных событий
apiStore.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  apiStore.itemsPerPage = newVal
})

// Конфигурация полей фильтров
const filterConfigs: IFilterConfig[] = [
  { key: 'advanced', labelPrefix: '', type: 'text', getCustomLabel: () => 'Условия поиска' },
  { key: 'departments', labelPrefix: 'Отделение: ', type: 'tree' },
  { key: 'event_types', labelPrefix: 'Вид: ', type: 'tree' },
  { key: 'categories', labelPrefix: 'Категория: ', type: 'tree' }, // Категория обычно скрыта, но есть в стейте
  { key: 'risk', labelPrefix: 'Риск: ', type: 'tree' },
  { key: 'numbers', labelPrefix: 'Номер: ', type: 'number' },
  { key: 'coordinators', labelPrefix: 'Координатор: ', type: 'tree' },
  { key: 'creators', labelPrefix: 'Регистратор: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: (val: string) => getStatusLabel(val) },
  { key: 'date_time', labelPrefix: 'Дата: ', type: 'date_range', rangeStartKey: 'created_after', rangeEndKey: 'created_before' },
  { key: 'is_active', labelPrefix: '', type: 'boolean', getCustomLabel: () => 'Мои активные НС' },
  { key: 'requires_action', labelPrefix: 'Требует действия: ', type: 'boolean' },
  { key: 'is_actual', labelPrefix: '', type: 'boolean', silent: true },
]

// Инициализация Composable
const tableLogic = useTableFilters({
  filters: computed(() => apiStore.filters),
  searchChoices: computed(() => apiStore.searchChoices) as any,
  filterChoices: computed(() => apiStore.filterChoices) as any,
  filterLoading: apiStore.filterLoading,
  fetchFilterChoices: (key: string) => apiStore.fetchFilterChoices(key, undefined, undefined),
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => apiStore.fetchFilteredAdverseEvents(cursor, limit, throwOnError),
  realtimeEnabled: true,
  currentSortString,
  itemsPerPage,
  resetFilters: () => apiStore.resetFilters(),
  nextCursor: nextAdverseEventsCursor,
  loadedItemsCount: computed(() => apiStore.adverseEvents.length),
  totalItemsCount: computed(() => apiStore.totalAdverseEventsCount),
  sortMap: {
    number: 'number',
    department: 'department',
    type: 'event_type',
    created_by: 'created_by',
    risk: 'risk_level',
    coordinator: 'coordinator_name',
    date: 'date_time',
  },
  filterConfigs,
})

const isHideCompleted = computed({
  get: () => apiStore.filters.is_actual === true,
  set: (val: boolean) => {
    apiStore.filters.is_actual = val ? true : null
    hideCompleted.value = val
    tableLogic.triggerServerFilter()
  },
})

// Заголовки таблицы нежелательных событий с метаданными
const headers = computed(() => [
  { title: '№', key: 'number', sortable: true, filterType: 'number', filterKey: 'numbers' },
  { title: 'Отделение', key: 'department', sortable: true, filterType: 'tree', filterKey: 'departments' },
  { title: 'Вид НС', key: 'type', sortable: true, filterType: 'tree', filterKey: 'event_types' },
  { title: 'Координатор', key: 'coordinator', sortable: true, filterType: 'tree', filterKey: 'coordinators' },
  { title: 'Регистратор', key: 'created_by', sortable: true, filterType: 'tree', filterKey: 'creators' },
  { title: 'Риск', key: 'risk', sortable: true, filterType: 'tree', filterKey: 'risk' },
  { title: 'Дата', key: 'date', sortable: true, filterType: 'date_range', filterKey: 'date_time' },
  { title: 'Время', key: 'time', sortable: false },
  { title: 'Статус', key: 'status', sortable: false, filterType: 'tree', filterKey: 'status' },
] as any[])

const displayedItems = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value

  return apiStore.adverseEvents.slice(start, end)
})

/* Онбординг: демо-НС первыми в таблице и действия, которыми сценарий открывает меню, модалки, панель фильтра и форму */
const onboardingStore = useOnboardingStore()
const { rows: tableRows } = useOnboardingDemoRows('adverse_event', displayedItems)
const onboardingFormDemoId = computed(() => onboardingStore.activeDemoItems[ADVERSE_FORM_DEMO_INDEX]?.list.id ?? null)
const isActionsMenuOpen = ref<number | undefined>(undefined)
const isSettingsMenuOpen = ref(false)

useOnboardingContext({
  names: [ADVERSE_CONTEXTS.table],
  scenarioId: 'adverseEvents',
  root: () => document.body,
  actions: {
    openActionsMenu: () => {
      if (smAndDown.value)
        isActionsMenuOpen.value = 0
    },
    openSettingsMenu: () => { isSettingsMenuOpen.value = true },
    closeSettingsMenu: () => { isSettingsMenuOpen.value = false },
    openTypesModal: () => { isAdminSettingsOpen.value = true },
    closeTypesModal: () => { isAdminSettingsOpen.value = false },
    openProbabilityModal: () => { isProbabilitySettingsOpen.value = true },
    closeProbabilityModal: () => { isProbabilitySettingsOpen.value = false },
    openFilterPanel: () => { searchFilterRef.value?.openPanel() },
    closeFilterPanel: () => { searchFilterRef.value?.closePanel() },
    openDemoForm: async () => {
      if (onboardingFormDemoId.value !== null)
        await router.push({ name: ERouteNames.ADVERSE_EVENT_DETAIL, params: { id: onboardingFormDemoId.value }, query: { [ONBOARDING_DEMO_QUERY]: '1' } })
    },
    closeForm: async () => { await router.push({ name: ERouteNames.ADVERSE_EVENTS }) },
  },
})

// Автостарт только на самой таблице: при прямом заходе в карточку НС обучение не перебивает работу
onMounted(() => {
  if (router.currentRoute.value.name === ERouteNames.ADVERSE_EVENTS)
    void onboardingStore.autoStart('adverseEvents')
})

/**
 * Обработчик клика по строке таблицы
 */
const handleRowClick = async (item: IAdverseEvent) => {
  await router.push({
    name: ERouteNames.ADVERSE_EVENT_DETAIL,
    params: { id: item.id },
  })
}

/**
 * Свойства строк таблицы для обработки кликов
 */
const rowProps = (item: { item: IAdverseEvent }) => {
  return {
    onClick: () => handleRowClick(item.item),
    class: {
      'row--requires-action': item.item.requires_action === true,
    },
    'data-onboarding': item.item.id === onboardingFormDemoId.value ? ADVERSE_ANCHORS.table.demoRow : undefined,
  }
}

const goToAdverseStats = async () => {
  const state = adverseSearchState(apiStore.filters)
  await router.push({ name: ERouteNames.ADVERSE_STATS, query: {
    search: state.search || undefined,
    advanced: state.filter.conditions.length ? JSON.stringify(state.filter) : undefined,
  } })
}

/**
 * Создает новое событие и переходит на страницу создания
 */
const createNewAdverseEvent = () => {
  apiStore.resetCurrentAdverseEvent()
  router.push({ name: ERouteNames.CREATE_ADVERSE_EVENT })
}

/**
 * Возвращает читаемое название статуса
 */
function getStatusLabel(status: string) {
  if (!status) {
    return 'Ожидает'
  }
  const statuses = apiStore.responsibilityEntriesStatuses

  return (statuses as Record<string, string>)[status] || status
}

const reportFilters = computed(() => adverseReportFilters(apiStore.filters))
const { download: downloadExport, isExporting } = useExportUrl(
  '/api/adverse/adverse-event/export/',
  () => ({}),
  () => reportFilters.value,
)
</script>

<template>
  <header :class="{ 'd-flex flex-column gap-2 mb-4': smAndDown } ">
    <div :class="smAndDown ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center justify-space-between w-100'">
      <VBtn
        :class="{ 'w-100': smAndDown }"
        variant="elevated"
        :loading="feedbackStore.isGlobalLoading"
        prepend-icon="ri-add-line"
        class="general-button"
        :data-onboarding="ADVERSE_ANCHORS.table.create"
        @click="createNewAdverseEvent"
      >
        Зарегистрировать НС
      </VBtn>

      <!-- На мобильных скрываем кнопки навигации/действий в Expansion Panel -->
      <VExpansionPanels v-if="smAndDown" v-model="isActionsMenuOpen" class="w-100 mt-1">
        <VExpansionPanel>
          <VExpansionPanelTitle class="d-flex align-center py-2 px-4" style="min-height: 48px;">
            <div class="d-flex align-center gap-2">
              <VIcon icon="ri-menu-2-line" size="20" />
              <span class="text-subtitle-1 font-weight-medium">Меню действий</span>
            </div>
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <div class="d-flex flex-column gap-2 w-100">
              <VBtn
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-pie-chart-line"
                :data-onboarding="ADVERSE_ANCHORS.table.statistics"
                @click="router.push({ name: ERouteNames.ADVERSE_GRAPH })"
              >
                Статистика
              </VBtn>

              <VBtn
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-line-chart-line"
                :data-onboarding="ADVERSE_ANCHORS.table.reports"
                @click="goToAdverseStats"
              >
                Отчеты
              </VBtn>

              <!-- Меню настроек НС (мобильная версия) -->
              <VMenu
                v-if="permissionsStore.modules.adverse?.type_edit || permissionsStore.modules.adverse?.probability"
                v-model="isSettingsMenuOpen"
                location="bottom end"
                offset="5px"
              >
                <template #activator="{ props: menuProps }">
                  <VBtn
                    v-bind="menuProps"
                    :data-onboarding="ADVERSE_ANCHORS.table.settings"
                    class="w-100"
                    variant="outlined"
                    color="secondary"
                    prepend-icon="ri-settings-4-line"
                  >
                    Настройки
                  </VBtn>
                </template>
                <VList min-width="220" :data-onboarding="ADVERSE_ANCHORS.table.settingsList">
                  <VListItem
                    v-if="permissionsStore.modules.adverse?.type_edit"
                    title="Вид/подвид НС"
                    @click="isAdminSettingsOpen = true"
                  />
                  <VListItem
                    v-if="permissionsStore.modules.adverse?.probability"
                    title="Вероятности/риски"
                    @click="isProbabilitySettingsOpen = true"
                  />
                </VList>
              </VMenu>

              <VBtn
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-share-2-line"
                :data-onboarding="ADVERSE_ANCHORS.table.export"
                :loading="isExporting"
                @click="downloadExport"
              >
                Выгрузить
              </VBtn>
              <div class="d-flex justify-end gap-2">
                <ReportTemplateMenu
                  object-type="adverse_event"
                  context="collection"
                  variant="toolbar"
                  :filters="reportFilters"
                  :sort="currentSortString"
                />
                <OnboardingLaunchButton scenario="adverseEvents" />
              </div>
            </div>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>

      <!-- На ПК показываем обычный горизонтальный ряд кнопок -->
      <div
        v-else
        class="d-flex align-center gap-2"
      >
        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="ri-pie-chart-line"
          :data-onboarding="ADVERSE_ANCHORS.table.statistics"
          @click="router.push({ name: ERouteNames.ADVERSE_GRAPH })"
        >
          Статистика
        </VBtn>

        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="ri-line-chart-line"
          :data-onboarding="ADVERSE_ANCHORS.table.reports"
          @click="goToAdverseStats"
        >
          Отчеты
        </VBtn>

        <!-- Меню настроек НС (десктоп версия) -->
        <VMenu
          v-if="permissionsStore.modules.adverse?.type_edit || permissionsStore.modules.adverse?.probability"
          v-model="isSettingsMenuOpen"
          location="bottom end"
          offset="5px"
        >
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              :data-onboarding="ADVERSE_ANCHORS.table.settings"
              variant="outlined"
              color="secondary"
              class="bg-surface"
              :class="{ 'w-100': smAndDown }"
              prepend-icon="ri-settings-4-line"
            >
              Настройки
            </VBtn>
          </template>
          <VList min-width="220" :data-onboarding="ADVERSE_ANCHORS.table.settingsList">
            <VListItem
              v-if="permissionsStore.modules.adverse?.type_edit"
              title="Вид/подвид НС"
              @click="isAdminSettingsOpen = true"
            />
            <VListItem
              v-if="permissionsStore.modules.adverse?.probability"
              title="Вероятности/риски"
              @click="isProbabilitySettingsOpen = true"
            />
          </VList>
        </VMenu>

        <VIconBtn
          color="secondary"
          class="rounded-sm"
          icon="ri-share-2-line"
          :icon-size="20"
          variant="outlined"
          :rounded="false"
          :size="38"
          :data-onboarding="ADVERSE_ANCHORS.table.export"
          :loading="isExporting"
          @click="downloadExport"
        />
        <ReportTemplateMenu
          object-type="adverse_event"
          context="collection"
          variant="toolbar"
          :filters="reportFilters"
          :sort="currentSortString"
        />
        <OnboardingLaunchButton scenario="adverseEvents" />
      </div>
    </div>
  </header>

  <div class="wrapper">
    <div>
      <div
        class="adverse-search-toolbar pa-4"
        :class="{ 'adverse-search-toolbar--stacked': smAndDown }"
      >
        <div class="adverse-search-control">
          <AdverseSearchFilter ref="searchFilterRef" @apply="tableLogic.triggerServerFilter()" />
        </div>

        <div class="adverse-search-actions d-flex align-center gap-3">
          <VBtn
            v-if="tableLogic.hasActiveFilters.value"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            class="adverse-search-reset text-color-secondary"
            :data-onboarding="ADVERSE_ANCHORS.table.resetFilters"
            height="48"
            :loading="feedbackStore.isGlobalLoading"
            @click="tableLogic.renewDataAndResetFilters()"
          >
            Сбросить фильтры
          </VBtn>

          <VSwitch
            v-model="isHideCompleted"
            :data-onboarding="ADVERSE_ANCHORS.table.hideCompleted"
            label="Скрыть завершенные"
            density="compact"
            color="primary"
            hide-details
            class="flex-grow-0"
          />
        </div>
      </div>

      <VDataTableServer
        :items="tableRows"
        :items-length="apiStore.totalAdverseEventsCount"
        :headers="headers as any"
        :items-per-page="itemsPerPage"
        :row-props="rowProps"
        class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
      >
        <template #no-data>
          <div class="text-center text-italic py-4">
            Данные отсутствуют
          </div>
        </template>

        <!-- ДИНАМИЧЕСКИЕ ЗАГОЛОВКИ ФИЛЬТРОВ ИЗ COMPOSABLE -->
        <template
          v-for="col in headers.filter(h => h.filterType)"
          :key="col.key"
          #[`header.${col.key}`]="{ column }"
        >
          <div class="header-content d-flex align-center">
            <span>{{ column.title }}</span>

            <!-- Иконка сортировки -->
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon(col.key)"
              :class="{
                'is-active': tableLogic.isSortActive(col.key),
                'is-desc': tableLogic.isSortDesc(col.key),
              }"
            />

            <VIconBtn
              variant="plain"
              size="small"
              class="filter-icon-btn"
              icon="ri-filter-line"
              :aria-label="`Фильтр: ${column.title}`"
              :data-onboarding="ADVERSE_ANCHORS.table.filterPrefix + col.key"
              @click.stop="searchFilterRef?.openField(col.filterKey === 'risk' ? 'risk_level' : col.filterKey)"
            />
          </div>
        </template>
        <!-- КОНЕЦ ДИНАМИЧЕСКИХ ЗАГОЛОВКОВ -->

        <!-- Статические ячейки таблицы -->
        <template #[`header.time`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <template #[`item.number`]="{ item }">
          <span>{{ item.number }}</span>
        </template>

        <template #[`item.department`]="{ item }">
          <span class="cell-content">{{ item.department?.short_name || item.department?.name || 'Отделение не указано' }}</span>
        </template>

        <template #[`item.type`]="{ item }">
          <CollapsibleText :text="apiStore.getEventTypeName(item.event_type)" />
        </template>

        <template #[`item.coordinator`]="{ item }">
          <ShortFio
            :person="item.coordinator"
            empty-text="Не назначен"
          />
        </template>

        <template #[`item.created_by`]="{ item }">
          <ShortFio
            :person="item.created_by"
            empty-text="—"
          />
        </template>

        <template #[`item.risk`]="{ item }">
          <div class="d-flex align-center gap-2">
            <span
              v-if="item.risk"
              class="risk-dot"
              :style="{ backgroundColor: item.risk_color }"
            />
            <span>{{ item.risk || '—' }}</span>
          </div>
        </template>

        <template #[`item.date`]="{ item }">
          <span>{{ formatDateOnly(item.date_time) }}</span>
        </template>

        <template #[`item.time`]="{ item }">
          <span>{{ formatTimeOnly(item.date_time) }}</span>
        </template>

        <template #[`item.status`]="{ item }">
          <VChip
            class="status-badge"
            :class="item.status"
            size="small"
          >
            {{ getStatusLabel(item.status) }}
          </VChip>
        </template>

        <!-- Кастомный Footer (сохраняем инвариантность) -->
        <template #bottom>
          <div
            class="custom-data-table-footer"
            :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
          >
            <VDataTableFooter
              show-current-page
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>

  <EmployeeProfileSidebar
    v-model="tableLogic.isEmployeeInfoModalOpen.value"
    :employee-id="tableLogic.selectedEmployeeId.value"
  />

  <AdverseEventTypesModal v-model="isAdminSettingsOpen" />

  <AdverseProbabilityModal v-model="isProbabilitySettingsOpen" />
</template>

<style scoped lang="scss">
.adverse-search-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
}

.adverse-search-control {
  min-inline-size: 0;

  &:only-child {
    grid-column: 1 / -1;
  }
}

.adverse-search-actions {
  min-block-size: 48px;
}

.adverse-search-toolbar--stacked {
  grid-template-columns: minmax(0, 1fr);

  .adverse-search-actions {
    flex-direction: column;
    align-items: stretch !important;
  }

  .adverse-search-reset {
    inline-size: 100%;
  }
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.v-list) {
  padding: 0 !important;
}

/* Стили для risk-dot */
.risk-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

/* Адаптивные стили */
.cursor-pointer {
  cursor: pointer;
}

.text-italic {
  font-style: italic;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gap-2 {
  gap: 8px;
}

.col-span-12 {
  grid-column: span 12;
}

header {
  background: rgb(var(--v-theme-background));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }
}

:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

/* Минимальная ширина колонок */
:deep(.v-data-table__th:nth-child(1)) { min-width: 6rem !important; }
:deep(.v-data-table__th:nth-child(2)) { min-width: 7.5rem !important; }
:deep(.v-data-table__th:nth-child(3)) { min-width: 20rem !important; }
:deep(.v-data-table__th:nth-child(4)) { min-width: 11.25rem !important; }
:deep(.v-data-table__th:nth-child(5)) { min-width: 10rem !important; }
:deep(.v-data-table__th:nth-child(6)) { min-width: 4.375rem !important; }
:deep(.v-data-table__th:nth-child(7)) { min-width: 4.375rem !important; }
:deep(.v-data-table__th:nth-child(8)) { min-width: 3.75rem !important; }
:deep(.v-data-table__th:nth-child(9)) { min-width: 8rem !important; }

:deep(.v-data-table__th) {
  white-space: nowrap;
}

/* Скрываем стандартные стрелки Vuetify */
:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

/* Настройка нашей иконки */
.custom-sort-icon {
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

/* Эффект при наведении на ячейку заголовка */
:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

:deep(.custom-data-table tr.row--requires-action > .v-data-table__td:first-child) {
  border-inline-start: 4px solid rgb(var(--v-theme-error)) !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 16px 16px 16px !important;
}
</style>
